import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import LayeredStack from "@/components/LayeredStack";
import { PageLoader } from "@/components/Kit";
import { User, ShieldCheck, Trophy, UserPlus, MessageSquare, Bot } from "lucide-react";

export default function Profile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [bio, setBio] = useState("");
  const [editing, setEditing] = useState(false);

  const load = () => {
    const url = userId ? `/profile/${userId}` : "/me/profile";
    api.get(url).then((r) => { setD(r.data); setBio(r.data.bio || ""); })
      .catch((e) => setErr(apiError(e.response?.data?.detail)));
  };
  useEffect(() => { setD(null); setErr(null); load(); }, [userId]);

  const saveBio = async () => {
    try { await api.put("/me/profile", { bio }); toast.success("Bio updated."); setEditing(false); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addFriend = async () => {
    try { await api.post("/friends/request", { to_user: d.user_id }); toast.success("Friend request sent."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (err) return <div data-testid="profile-error" className="panel p-8 text-center text-muted-foreground">{err}</div>;
  if (!d) return <PageLoader label="Loading pilot dossier" />;

  return (
    <div data-testid="profile-page">
      <span className="label-cap text-primary flex items-center gap-2"><User className="w-4 h-4" /> Pilot Dossier</span>
      <div className="panel-glow p-6 hud-corner mt-3 flex flex-col sm:flex-row gap-6 items-start">
        <div className="w-20 h-20 rounded-2xl bg-primary/15 grid place-items-center text-primary shrink-0"><User className="w-10 h-10" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-head font-black text-3xl tracking-tighter">{d.name}</h1>
            <span className="label-cap flex items-center gap-1 text-secondary"><ShieldCheck className="w-3.5 h-3.5" /> SCL-{d.scl}</span>
          </div>
          {editing ? (
            <div className="mt-3">
              <textarea data-testid="bio-input" value={bio} maxLength={500} onChange={(e) => setBio(e.target.value)} rows={3}
                className="w-full px-3 py-2 rounded-[var(--radius)] bg-muted border border-border text-sm focus:outline-none focus:border-primary" placeholder="Write your pilot backstory…" />
              <div className="flex gap-2 mt-2"><button onClick={saveBio} data-testid="save-bio-btn" className="btn-primary text-xs">Save</button><button onClick={() => setEditing(false)} className="btn-ghost text-xs">Cancel</button></div>
            </div>
          ) : (
            <p className="text-muted-foreground mt-2 text-sm whitespace-pre-wrap">{d.bio || "No backstory logged yet."}</p>
          )}
          <div className="flex gap-2 mt-4">
            {d.is_self ? (
              <button onClick={() => setEditing(true)} data-testid="edit-bio-btn" className="btn-secondary text-xs">Edit Bio</button>
            ) : (
              <>
                {!d.is_friend && <button onClick={addFriend} data-testid="add-friend-btn" className="btn-primary text-xs"><UserPlus className="w-3.5 h-3.5" /> Add Friend</button>}
                <button onClick={() => navigate(`/app/mail?to=${d.user_id}`)} data-testid="message-btn" className="btn-secondary text-xs"><MessageSquare className="w-3.5 h-3.5" /> Message</button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        <div className="panel p-4">
          <div className="label-cap mb-3 flex items-center gap-1.5"><Bot className="w-3.5 h-3.5" /> Robots ({d.robots.length})</div>
          {d.robots.length === 0 ? <p className="text-sm text-muted-foreground">No robots fabricated.</p> : (
            <div className="flex flex-wrap gap-4">
              {d.robots.map((r, i) => (
                <div key={i} className="text-center"><LayeredStack layers={r.layers} core={r.core_color} rig={r.rig} cpu={r.cpu} size={80} /><div className="text-xs font-bold mt-1">{r.name}</div></div>
              ))}
            </div>
          )}
        </div>
        <div className="panel p-4">
          <div className="label-cap mb-3 flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Trophy Case</div>
          {d.trophies_hidden ? <p className="text-sm text-muted-foreground">This pilot's trophy case is private.</p>
            : <p className="text-sm text-muted-foreground">No trophies yet — win weekly leaderboards to earn them.</p>}
        </div>
      </div>
    </div>
  );
}
